import type { HTMLInputTypeAttribute } from 'react';

import { File, FileAudio2, FileImage, FileText, FileVideo } from 'lucide-react';

export const WILDCARD = '*';
export const IMAGE_SUBTYPES = [
    'jpg',
    'jpeg',
    'png',
    'webp',
    'bmp',
    'svg',
] as const;
export const AUDIO_SUBTYPES = ['mpeg', 'ogg', 'wav', 'midi'] as const;
export const VIDEO_SUBTYPES = ['mp4', 'webm', 'ogg'] as const;
export const TEXT_SUBTYPES = ['plain', 'html'] as const;
export const APPLICATION_SUBTYPES = ['pdf', 'json', 'xml', 'zip', 'vnd.ms-excel'] as const;

export const DEFAULT_FILE_ORDER = 9999 as const;
export const DEFAULT_IMAGE_QUALITY = 'OriginalWebp' as const;

export type ImageSubtypes = (typeof IMAGE_SUBTYPES)[number];
export type AudioSubtypes = (typeof AUDIO_SUBTYPES)[number];
export type VideoSubtypes = (typeof VIDEO_SUBTYPES)[number];
export type TextSubtypes = (typeof TEXT_SUBTYPES)[number];
export type ApplicationSubtypes = (typeof APPLICATION_SUBTYPES)[number];

export const SUBTYPES = [
    WILDCARD,
    ...IMAGE_SUBTYPES,
    ...AUDIO_SUBTYPES,
    ...VIDEO_SUBTYPES,
    ...TEXT_SUBTYPES,
    ...APPLICATION_SUBTYPES,
] as const;

export type Subtypes = (typeof SUBTYPES)[number];

export const FILE_TYPES = {
    image: {
        icon: FileImage,
        subtypes: IMAGE_SUBTYPES,
    },
    audio: {
        icon: FileAudio2,
        subtypes: AUDIO_SUBTYPES,
    },
    video: {
        icon: FileVideo,
        subtypes: VIDEO_SUBTYPES,
    },
    text: {
        icon: FileText,
        subtypes: TEXT_SUBTYPES,
    },
    application: {
        icon: FileText,
        subtypes: APPLICATION_SUBTYPES,
    },
    all: {
        icon: File,
        subtypes: SUBTYPES,
    },
} as const;

export type FileTypes = keyof typeof FILE_TYPES;
export type FileTypesSubtype<TFileType extends FileTypes>
    = (typeof FILE_TYPES)[TFileType]['subtypes'][number];

export function isFileSubtype<TFileType extends FileTypes>(
    subtype: Subtypes,
    fileType: TFileType,
): subtype is FileTypesSubtype<TFileType> {
    // @ts-expect-error: This is a workaround to allow the use of the wildcard type
    return FILE_TYPES[fileType].subtypes.includes(subtype);
}

export function isValidType(type: string): type is FileTypes {
    return Object.keys(FILE_TYPES).includes(type);
}

export function isImageSubtype(subtype: Subtypes): subtype is ImageSubtypes {
    return isFileSubtype(subtype, 'image');
}

export function isAudioSubtype(subtype: Subtypes): subtype is AudioSubtypes {
    return isFileSubtype(subtype, 'audio');
}

export function isVideoSubtype(subtype: Subtypes): subtype is VideoSubtypes {
    return isFileSubtype(subtype, 'video');
}

export function isTextSubtype(subtype: Subtypes): subtype is TextSubtypes {
    return isFileSubtype(subtype, 'text');
}

export function isApplicationSubtype(
    subtype: Subtypes,
): subtype is ApplicationSubtypes {
    return isFileSubtype(subtype, 'application');
}

export function isWildcardSubtype(
    subtype: Subtypes,
): subtype is typeof WILDCARD {
    return subtype === WILDCARD;
}

export function isAllSubtype(
    subtype: Subtypes,
): subtype is FileTypesSubtype<'all'> {
    return isFileSubtype(subtype, 'all');
}

export function getFileTypeIcon(fileType: FileTypes) {
    return FILE_TYPES[fileType].icon;
}

function determineFileTypeBySubtype(subtype: Subtypes) {
    if (isImageSubtype(subtype))
        return 'image';
    if (isAudioSubtype(subtype))
        return 'audio';
    if (isVideoSubtype(subtype))
        return 'video';
    if (isTextSubtype(subtype))
        return 'text';
    if (isApplicationSubtype(subtype))
        return 'application';
    if (isAllSubtype(subtype))
        return 'all';
}

export function getFileTypeBySubtypes(
    fileSubtypes: Subtypes[] | Subtypes,
): FileTypes {
    const subtypes = Array.isArray(fileSubtypes)
        ? fileSubtypes
        : [fileSubtypes];

    const fileType = subtypes
        .map(determineFileTypeBySubtype)
        .reduce<FileTypes | null>((acc, fileType) => {
            if (acc === null)
                return fileType ?? null;
            if (!fileType || acc !== fileType)
                return 'all';
            return fileType;
        }, null);

    return fileType ?? 'all';
}

export function getFileTypeByFile(file: File) {
    const fileType = file?.type?.split('/')[0] as FileTypes;
    return fileType;
}

export function makeAcceptString({
    subtype,
    type,
}: {
    subtype: Subtypes | Subtypes[];
    type?: FileTypes;
}) {
    const subtypes = Array.isArray(subtype) ? subtype : [subtype];

    const containsWildcard = subtypes.some(isWildcardSubtype);

    if (!type && containsWildcard)
        return '*/*';
    if (containsWildcard)
        return `${type}/*`;

    const mimeTypes: string[] = [];

    subtypes.forEach((subtype) => {
        const fileType = determineFileTypeBySubtype(subtype);

        if (!fileType)
            return;
        if (type && fileType !== type)
            return;

        mimeTypes.push(`${fileType}/${subtype}`);
    });

    return mimeTypes.join(',');
}

export function determineInputType(
    props:
        | {
            subtype: Subtypes | Subtypes[];
        }
        | {
            type: FileTypes;
        },
): HTMLInputTypeAttribute {
    let type: HTMLInputTypeAttribute = 'file';

    if ('subtype' in props) {
        const subtypes
            = typeof props.subtype === 'string' ? [props.subtype] : props.subtype;

        const fileType = getFileTypeBySubtypes(subtypes);

        if (fileType === 'image')
            type = 'image';
    }
    else {
        if (props.type === 'image')
            type = 'image';
    }

    return type;
}
